import axios from "axios";
import { API_BASE_URL, CLIENT_BASE_PATH } from "../../environment";

export type BlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category?: string;
  author?: string;
  image?: string;
  imageAlt?: string;
  tags?: string[];
  seoTitle?: string;
  seoDescription?: string;
  canonicalUrl?: string;
  publishedAt?: string;
  status?: string;
};

export const fallbackBlogPosts: BlogPost[] = [
  {
    id: "makkah-to-madinah-taxi-guide",
    title: "Makkah to Madinah Taxi Guide for Umrah Travellers",
    slug: "makkah-to-madinah-taxi-guide",
    excerpt:
      "Plan a private taxi from Makkah to Madinah with practical pickup, luggage, timing, and family travel tips.",
    content:
      "A private taxi from Makkah to Madinah is one of the easiest ways for Umrah travellers to move between the holy cities with luggage and family members.\n\nFor a smoother journey, confirm your hotel pickup point, passenger count, luggage size, and preferred pickup time before travel. Early morning departures are often calmer, while evening transfers can work well after checkout.\n\nAlharamain Cab helps pilgrims book sedan, SUV, Hiace, and bus options for direct transfers between hotels, airports, and Ziyarat routes.",
    category: "Umrah Transport",
    author: "Alharamain Cab",
    image: `${CLIENT_BASE_PATH}/assets/img/banner.jpg`,
    imageAlt: "Private taxi service between Makkah and Madinah",
    tags: ["Makkah taxi", "Madinah taxi", "Umrah transport"],
    publishedAt: "2026-06-01T00:00:00.000Z",
  },
  {
    id: "jeddah-airport-to-makkah-transfer",
    title: "Jeddah Airport to Makkah Transfer: What to Know",
    slug: "jeddah-airport-to-makkah-transfer",
    excerpt:
      "A simple checklist for booking a Jeddah airport taxi to Makkah after a long flight.",
    content:
      "After arriving at Jeddah airport, most pilgrims want a direct and comfortable transfer to their Makkah hotel.\n\nShare your flight number, arrival terminal, hotel name, and WhatsApp contact when booking. This helps the driver track timing and meet you at the correct point.\n\nPrivate airport transfers are helpful for families, elderly passengers, and groups carrying Ihram bags or extra luggage.",
    category: "Airport Transfers",
    author: "Alharamain Cab",
    image: `${CLIENT_BASE_PATH}/assets/img/banner.jpg`,
    imageAlt: "Jeddah airport taxi transfer to Makkah",
    tags: ["Jeddah airport", "Makkah transfer", "Airport taxi"],
    publishedAt: "2026-05-25T00:00:00.000Z",
  },
  {
    id: "ziyarat-taxi-service-tips",
    title: "How to Book a Ziyarat Taxi Service in Saudi Arabia",
    slug: "ziyarat-taxi-service-tips",
    excerpt:
      "Useful tips for arranging Ziyarat trips in Makkah, Madinah, and nearby holy sites.",
    content:
      "Ziyarat taxi services are best planned around your prayer schedule, group size, and the sites you want to visit.\n\nChoose a vehicle with enough space for all passengers, confirm the route in advance, and allow extra time during busy Hajj and Umrah periods.\n\nA private Ziyarat ride gives your group more flexibility than joining a fixed shared schedule.",
    category: "Ziyarat",
    author: "Alharamain Cab",
    image: `${CLIENT_BASE_PATH}/assets/img/banner.jpg`,
    imageAlt: "Ziyarat taxi service for pilgrims",
    tags: ["Ziyarat taxi", "Makkah Ziyarat", "Madinah Ziyarat"],
    publishedAt: "2026-05-18T00:00:00.000Z",
  },
];

const normalizePost = (post: any): BlogPost => ({
  ...post,
  id: post.id || post.slug,
  tags: Array.isArray(post.tags) ? post.tags : [],
});

export const getBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/blogs/list`, {
      limit: 100,
    });
    const posts = Array.isArray(response.data) ? response.data : response.data?.data || [];

    return posts.length ? posts.map(normalizePost) : fallbackBlogPosts;
  } catch (error) {
    console.error("Error fetching blog posts:", error);

    return fallbackBlogPosts;
  }
};

export const getBlogPost = async (slug: string): Promise<BlogPost | null> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/blogs/${slug}`);
    if (response.data?.status === "draft") {
      return null;
    }

    return normalizePost(response.data);
  } catch (error) {
    const fallbackPost = fallbackBlogPosts.find(post => post.slug === slug);

    return fallbackPost || null;
  }
};

export const postUrl = (post: BlogPost) => `/blog/${post.slug}`;
